import type {
  DatePickerProps as BaseDatePickerProps,
  LocaleCode,
} from '@ur-solutions/react-components'
import { DatePicker as BaseDatePicker } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useRef } from 'react'
import styled, { css } from 'styled-components'
import { getLocale } from '@/utils/date-fns'
import { useDatePickerNavigation } from '../../utils/hooks/useDatePickerNavigation/useDatePickerNavigation'

const ErrorText = styled.div`
  position: absolute;
  top: -2px;
  right: 0;
  color: ${props => props.theme.colors.red};
  font-size: 0.7rem;
  white-space: nowrap;
  transform: translateY(-100%);
`

interface StyledDatePickerProps extends BaseDatePickerProps {
  grayed?: boolean
  disabled?: boolean
}

const StyledDatePicker = styled(BaseDatePicker)<StyledDatePickerProps>`
  .--date-picker-display:has(.--input-input:focus) .--input-icon-right,
  .--date-picker-display:has(.--input-input:focus) .--input-input {
    border-color: ${props => props.theme.colors.secondary};
  }

  .--date-picker-display {
    input {
      padding: 0 1rem;
      font-size: 1.2rem;
      border-top-left-radius: ${props => props.theme.sizes.defaultBorderRadius};
      border-bottom-left-radius: ${props =>
        props.theme.sizes.defaultBorderRadius};
    }

    .--input-input {
      padding: 0 1rem;
      font-size: 1.2rem;
      border-color: ${props => props.theme.colors.gray9};
    }

    .--input-icon-right {
      border-color: ${props => props.theme.colors.gray9};
      font-size: 1.5rem;
      border-top-right-radius: ${props =>
        props.theme.sizes.defaultBorderRadius};
      border-bottom-right-radius: ${props =>
        props.theme.sizes.defaultBorderRadius};

      i {
        color: ${props => props.theme.colors.tertiary};
      }
    }

    ${props =>
      props.disabled &&
      css`
        input {
          color: ${props.theme.colors.gray9};
        }

        [class*='--input-icon'] i {
          color: ${props.theme.colors.tertiary};
        }
      `};
  }

  .--date-picker-curtain {
    width: auto;
  }
  .--date-picker-curtain-month-info {
    display: flex;
    justify-content: center;
    font-size: 1rem;
    align-items: center;
    padding: 8px 65px;
    margin-bottom: 8px;
    position: relative;

    .--date-picker-curtain-month-info-month,
    .--date-picker-curtain-month-info-year {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px 8px;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s ease;
      font-weight: 600;
      position: relative;
    }

    .--date-picker-curtain-month-info-month {
      margin-right: 4px;
      min-width: 40px;
    }

    .--date-picker-curtain-month-info-year {
      margin-left: 4px;
      min-width: 30px;
    }
  }
`

interface DatePickerProps extends StyledDatePickerProps {}

/** @deprecated Use components/date-picker */
export const DatePicker: React.FC<DatePickerProps> = ({
  grayed,
  background: backgroundProp,
  value,
  onChange,
  ...datePickerProps
}) => {
  const locale = getLocale().code as LocaleCode
  const datePickerRef = useRef<HTMLDivElement>(null)
  const translations = useTranslate({ invalidDate: 'common.invalid-date' })
  const background = grayed ? 'quaternary' : backgroundProp ?? 'white'

  useDatePickerNavigation(datePickerRef, value, onChange)

  return (
    <span style={{ position: 'relative' }} ref={datePickerRef}>
      <ErrorText>{datePickerProps.dateErrorText}</ErrorText>
      <StyledDatePicker
        value={value}
        onChange={onChange}
        locale={locale}
        dateErrorText={translations.invalidDate}
        background={background}
        focusBorderColor="secondary"
        height="3.875rem"
        noAnimation
        noWeekNums
        $borderColor="gray9"
        displayDateFormat="dd.MM.yyyy"
        {...datePickerProps}
      />
    </span>
  )
}
